import {
    type MouseEventHandler,
    type ReactNode,
    useCallback,
    useEffect,
} from 'react';
import type { z } from 'zod';
import type { SetState } from '../types/setState';
import { isFunction } from '../utils/utilFunctions';
import type { AnyPath, ExtractRouterTypes } from './extractRouterTypes';

type ExtractRouteParamKey<P extends string> =
    P extends `:${infer RP}` ? RP
    : P extends `[${infer RP}]` ? RP
    : never;
type ExtractRouteParamKeys<R extends string> =
    R extends `${infer P}/${infer Rest}` ?
        ExtractRouteParamKey<P> | ExtractRouteParamKeys<Rest>
    :   ExtractRouteParamKey<R>;

export type RouteDefinitions<P extends AnyPath> = {
    [r in P]: {
        routeParams: z.Schema<
            Record<ExtractRouteParamKeys<r>, string | number>
        >;
        searchParams: z.Schema<Record<string, string | number | undefined>>;
    };
};

type To = { pathname: string; search: string | undefined };

export const createRouter = <
    PATH extends AnyPath,
    ROUTE extends PATH,
    const RD extends RouteDefinitions<PATH>,
>(
    routeDefinitions: RD,
    routerAdapters: {
        useCurrentRoute: () => PATH;
        useNavigate: () => (to: To) => void;
        Link: (props: {
            to: To;
            onAuxClick: MouseEventHandler;
            children: ReactNode;
        }) => ReactNode;
        useRouteParams: () => Record<string, string | undefined>;
        useSearchParams: () => [
            URLSearchParams,
            (
                newValue:
                    | URLSearchParams
                    | ((prevValues: URLSearchParams) => URLSearchParams)
            ) => void,
        ];
    }
) => {
    const {
        useCurrentRoute,
        useNavigate,
        Link,
        useRouteParams,
        useSearchParams,
    } = routerAdapters;

    type RouteTypes<R extends ROUTE> = ExtractRouterTypes<PATH, R, RD>;

    type RouteParams<R extends ROUTE> = RouteTypes<R>['RouteParams'];
    type SearchParams<R extends ROUTE> = RouteTypes<R>['SearchParams'];
    type RouteInfo<R extends ROUTE> = RouteTypes<R>['RouteInfo'];
    type MyNavigate = RouteTypes<ROUTE>['MyNavigate'];

    const createPathFromRouteInfo = <R extends ROUTE>({
        route,
        ...rest
    }: RouteInfo<R>): To => {
        let pathname: string = route;
        if ('routeParams' in rest) {
            Object.entries<string>(rest.routeParams as RouteParams<R>).forEach(
                ([key, value]) => {
                    pathname = pathname.replace(`:${key}`, value);
                    pathname = pathname.replace(`[${key}]`, value);
                }
            );
        }

        const search =
            'searchParams' in rest ?
                new URLSearchParams(
                    Object.entries(rest.searchParams as SearchParams<R>).filter(
                        ([_, value]) => Boolean(value)
                    ) as Array<[string, string]>
                ).toString()
            :   undefined;

        return { pathname, search };
    };

    const openNewWindow = <R extends ROUTE>(routeInfo: RouteInfo<R>): void => {
        const urlBase = window.location.origin;
        const { pathname, search } = createPathFromRouteInfo(routeInfo);
        const searchParams = search ? `?${search}` : '';
        window.open(`${urlBase}${pathname}${searchParams}`);
    };

    const useMyNavigate = (): MyNavigate => {
        const navigate = useNavigate();

        return useCallback<MyNavigate>(
            // TODO parse + onError cb?
            (routeInfo) => navigate(createPathFromRouteInfo(routeInfo)),
            [navigate]
        );
    };

    const MyLink = <R extends ROUTE>({
        children,
        ...rest
    }: { children: ReactNode } & RouteInfo<R>): ReactNode => {
        const routeInfo = rest as RouteInfo<R>;
        const to = createPathFromRouteInfo(routeInfo);
        const onAuxClick: MouseEventHandler = ({ button }) => {
            if (button === 1) {
                openNewWindow(routeInfo);
            }
        };

        return (
            <Link
                to={to}
                onAuxClick={onAuxClick}
            >
                {children}
            </Link>
        );
    };

    const useMyParams = <R extends ROUTE>(
        route: R
    ): {
        routeParams: RouteParams<R>;
        // TODO exclude SearchParams props when it's empty object
        searchParams: SearchParams<R>;
        setSearchParams: SetState<SearchParams<R>>;
    } => {
        const currentRoute = useCurrentRoute();
        const routeParams = useRouteParams();
        const [urlSearchParams, setUrlSearchParams] = useSearchParams();

        const {
            routeParams: routeParamsSchema,
            searchParams: searchParamsSchema,
        } = routeDefinitions[currentRoute];

        const setSearchParams: SetState<SearchParams<R>> = useCallback(
            (updater) => {
                setUrlSearchParams((prevValues) => {
                    const newValue =
                        isFunction(updater) ?
                            updater(
                                Object.fromEntries(
                                    prevValues
                                ) as SearchParams<R>
                            )
                        :   updater;
                    return new URLSearchParams(
                        Object.entries(newValue).filter(
                            ([_, value]) => value === 0 || Boolean(value)
                        ) as Array<[string, string]>
                    );
                });
            },
            [setUrlSearchParams]
        );

        useEffect(() => {
            if ((route as ROUTE) !== currentRoute) {
                throw new Error(
                    `useMyParams called with route '${route}', but actual route was '${currentRoute}'!`
                );
            }
        }, [currentRoute, route]);

        return {
            routeParams: routeParamsSchema.parse(routeParams) as RouteParams<R>,
            searchParams: searchParamsSchema.parse(
                Object.fromEntries(urlSearchParams)
            ) as SearchParams<R>,
            setSearchParams,
        };
    };

    const useValidateRoute = (): 'valid' | 'invalid' => {
        const currentRoute = useCurrentRoute();
        const routeParams = useRouteParams();
        const [searchParams] = useSearchParams();

        const {
            routeParams: routeParamsSchema,
            searchParams: searchParamsSchema,
        } = routeDefinitions[currentRoute];

        return (
                routeParamsSchema.safeParse(routeParams).success &&
                    searchParamsSchema.safeParse(
                        Object.fromEntries(searchParams)
                    ).success
            ) ?
                'valid'
            :   'invalid';
    };

    return {
        createPathFromRouteInfo,
        openNewWindow,
        useMyNavigate,
        MyLink,
        useMyParams,
        useValidateRoute,
    };
};
